import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/common/StatusBadge";
import { Plus, RefreshCw, Pause, Play, KeyRound, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";

export default function Operators() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ operator_name: "", legal_entity: "", jurisdiction: "MT", currency: "USD" });
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setBusy(true);
    const { data } = await api.get("/api/admin/operators");
    setItems(data);
    setBusy(false);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/api/admin/operators", form);
      toast.success("Operator created");
      setShowCreate(false);
      setForm({ operator_name: "", legal_entity: "", jurisdiction: "MT", currency: "USD" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleStatus = async (op) => {
    const action = op.status === "active" ? "suspend" : "activate";
    await api.post(`/api/admin/operators/${op.operator_id}/${action}`);
    toast.success(`Operator ${action}d`);
    load();
  };

  const rotate = async (op) => {
    const { data } = await api.post(`/api/admin/operators/${op.operator_id}/rotate-credentials`);
    toast.success("Credentials rotated");
    setSelected({ ...op, api_key: data.api_key, api_secret: data.api_secret, _just_rotated: true });
    load();
  };

  return (
    <div className="space-y-6" data-testid="operators-page">
      <div className="flex items-center justify-between">
        <div className="text-[var(--text-2)] text-sm">Manage casino operators integrating with the aggregator.</div>
        <button onClick={() => setShowCreate(true)} className="btn-gold px-4 py-2 rounded text-sm flex items-center gap-2" data-testid="create-operator-btn">
          <Plus size={14}/> New operator
        </button>
      </div>

      <div className="card-surface overflow-hidden">
        <div className="overflow-x-auto">
          <table className="aggr-table">
            <thead><tr><th>Name</th><th>Legal entity</th><th>Jurisdiction</th><th>Currency</th><th>API key</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {busy && <tr><td colSpan={7} className="text-center py-6"><Loader2 className="inline animate-spin" size={14}/></td></tr>}
              {!busy && items.map(op => (
                <tr key={op.operator_id} className="cursor-pointer" onClick={() => setSelected(op)}>
                  <td className="font-medium">{op.operator_name}</td>
                  <td className="text-xs text-[var(--text-2)]">{op.legal_entity}</td>
                  <td><span className="badge badge-muted">{op.jurisdiction}</span></td>
                  <td className="font-mono text-xs">{op.currency}</td>
                  <td className="font-mono text-xs text-[var(--text-2)]">{op.api_key.slice(0, 16)}…</td>
                  <td><StatusBadge status={op.status}/></td>
                  <td className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => toggleStatus(op)} className="p-2 hover:bg-[var(--surface-2)] rounded" title={op.status === "active" ? "Suspend" : "Activate"} data-testid={`toggle-status-${op.operator_id}`}>
                        {op.status === "active" ? <Pause size={14}/> : <Play size={14}/>}
                      </button>
                      <button onClick={() => rotate(op)} className="p-2 hover:bg-[var(--surface-2)] rounded" title="Rotate credentials" data-testid={`rotate-creds-${op.operator_id}`}>
                        <KeyRound size={14}/>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowCreate(false)}>
          <form onSubmit={create} onClick={(e) => e.stopPropagation()}
                className="card-surface w-full max-w-md p-6 space-y-4" data-testid="create-operator-form">
            <div className="text-lg font-semibold">Create operator</div>
            {[["operator_name", "Name"], ["legal_entity", "Legal entity"], ["jurisdiction", "Jurisdiction (MT, CW, GI…)"], ["currency", "Currency"], ["webhook_url", "Webhook URL"], ["callback_url", "Callback URL"]].map(([k, l]) => (
              <div key={k}>
                <label className="block text-xs uppercase tracking-widest text-[var(--text-3)] mb-1">{l}</label>
                <input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                       className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--border)] rounded text-sm focus:outline-none focus:border-[var(--gold)]"
                       data-testid={`create-op-${k}`} />
              </div>
            ))}
            <div className="flex gap-2 justify-end pt-2">
              <button type="button" onClick={() => setShowCreate(false)} className="btn-ghost px-4 py-2 rounded text-sm">Cancel</button>
              <button type="submit" className="btn-gold px-4 py-2 rounded text-sm">Create</button>
            </div>
          </form>
        </div>
      )}

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50" onClick={() => setSelected(null)}>
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-lg card-surface rounded-none p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="operator-detail">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-xs uppercase tracking-widest text-[var(--text-3)]">Operator</div>
                <div className="text-2xl font-semibold mt-1">{selected.operator_name}</div>
              </div>
              <StatusBadge status={selected.status}/>
            </div>
            <div className="space-y-4 text-sm">
              <Field label="Operator ID" value={selected.operator_id}/>
              <Field label="Legal entity" value={selected.legal_entity}/>
              <Field label="Jurisdiction" value={selected.jurisdiction}/>
              <Field label="Currency" value={selected.currency}/>
              <Field label="Webhook URL" value={selected.webhook_url || "—"}/>
              <Field label="Callback URL" value={selected.callback_url || "—"}/>
              <Field label="API Key" value={selected.api_key} mono copyable/>
              {selected._just_rotated && <Field label="API Secret (one-time)" value={selected.api_secret} mono copyable accent/>}
              <Field label="Allowed IPs" value={(selected.allowed_ips || []).join(", ") || "—"}/>
              <Field label="Assigned providers" value={(selected.assigned_providers || []).join(", ") || "—"}/>
            </div>
            <div className="flex gap-2 mt-8">
              <button onClick={() => rotate(selected)} className="btn-ghost px-3 py-2 rounded text-xs flex items-center gap-1" data-testid="detail-rotate-btn">
                <RefreshCw size={12}/> Rotate credentials
              </button>
              <button onClick={() => toggleStatus(selected)} className="btn-ghost px-3 py-2 rounded text-xs flex items-center gap-1" data-testid="detail-toggle-btn">
                {selected.status === "active" ? <><Pause size={12}/> Suspend</> : <><Play size={12}/> Activate</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, mono, copyable, accent }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="text-xs uppercase tracking-widest text-[var(--text-3)] flex-shrink-0">{label}</div>
      <div className={`text-right text-sm break-all ${mono ? "font-mono" : ""} ${accent ? "text-[var(--gold)]" : ""}`}>
        {value}
        {copyable && (
          <button onClick={() => { navigator.clipboard.writeText(value); toast.success("Copied"); }} className="ml-2 inline align-middle">
            <Copy size={11}/>
          </button>
        )}
      </div>
    </div>
  );
}
